import { Controller } from '@hotwired/stimulus'

const SCROLL_KEY = 'readingTrackerScrollY'
const RESTORE_KEY = 'readingTrackerShouldRestore'
const SCROLL_TO_WEEKLY_KEY = 'readingTrackerScrollToWeekly'
const MAX_RESTORE_ATTEMPTS = 30

const TRACKER_LINK_SELECTORS = [
  '.progress_nav-tracker',
  '.progress_circles-tracker',
  '.child_nav-tracker',
].join(', ')

const isDashboardPath = (pathname = window.location.pathname) => pathname.endsWith('/dashboard')

const persistScrollPosition = scrollY => {
  sessionStorage.setItem(SCROLL_KEY, String(scrollY))
  sessionStorage.setItem(RESTORE_KEY, 'true')
  sessionStorage.removeItem(SCROLL_TO_WEEKLY_KEY)
}

const clearScrollKeys = () => {
  sessionStorage.removeItem(SCROLL_KEY)
  sessionStorage.removeItem(RESTORE_KEY)
  sessionStorage.removeItem(SCROLL_TO_WEEKLY_KEY)
}

let lastScrollY = 0
let restoreGeneration = 0

const trackScroll = () => {
  lastScrollY = window.scrollY
}

const rememberedScrollY = () => Math.max(lastScrollY || 0, window.scrollY)

const destinationIsDashboard = url => {
  try {
    return isDashboardPath(new URL(url, window.location.origin).pathname)
  }
  catch {
    return false
  }
}

const cancelPendingRestore = () => {
  restoreGeneration += 1
}

const scheduleRestore = () => {
  const generation = restoreGeneration

  /* eslint-disable-next-line max-statements -- restore retries + weekly fallback */
  const run = (attempt = 0) => {
    if (generation !== restoreGeneration) {
      return
    }

    if (!isDashboardPath()) {
      return
    }

    const scrollY = sessionStorage.getItem(SCROLL_KEY)
    const shouldRestore = sessionStorage.getItem(RESTORE_KEY)
    const scrollToWeekly = sessionStorage.getItem(SCROLL_TO_WEEKLY_KEY)

    if (!shouldRestore && !scrollToWeekly) {
      return
    }

    if (scrollY !== null && shouldRestore !== null) {
      const targetY = parseInt(scrollY, 10)

      window.scrollTo(0, targetY)

      /*
       * Only restore on turbo:load (not turbo:render). During render the old
       * scroll is still in place, so an early restore clears keys and then
       * Turbo jumps to the top. Also cancel in-flight retries on before-visit
       * so a pending rAF cannot clear keys mid-navigation.
       */
      if (Math.abs(window.scrollY - targetY) > 40 && attempt < MAX_RESTORE_ATTEMPTS) {
        requestAnimationFrame(() => run(attempt + 1))
        return
      }
    }
    else if (scrollToWeekly) {
      const section = document.querySelector('.child_achievements-tracker')

      if (section) {
        section.scrollIntoView({ block: 'start' })
      }
    }

    if (generation === restoreGeneration) {
      clearScrollKeys()
    }
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => run())
  })
}

const saveScrollOnClick = event => {
  if (!isDashboardPath()) {
    return
  }

  const anchor = event.target.closest('a[href]')
  const tracker = document.querySelector('[data-controller~="reading-tracker"]')

  if (!anchor || !tracker || !tracker.contains(anchor)) {
    return
  }

  if (anchor.closest(TRACKER_LINK_SELECTORS) || !destinationIsDashboard(anchor.href)) {
    persistScrollPosition(rememberedScrollY())
  }
}

const saveScrollOnVisit = () => {
  if (!isDashboardPath()) {
    return
  }

  const scrollY = rememberedScrollY()

  if (scrollY > 0) {
    persistScrollPosition(scrollY)
  }
}

const captureScrollBeforeNavigation = () => {
  lastScrollY = Math.max(lastScrollY || 0, window.scrollY)
}

/*
 * Document-level listeners stay registered across Turbo visits so week
 * pagination scroll save/restore does not race Stimulus reconnect.
 */
if (!window.__readingTrackerScrollBound) {
  window.__readingTrackerScrollBound = true
  lastScrollY = window.scrollY

  window.addEventListener('scroll', trackScroll, { passive: true })
  document.addEventListener('pointerdown', captureScrollBeforeNavigation, true)
  document.addEventListener('turbo:click', saveScrollOnClick)
  document.addEventListener('turbo:before-visit', () => {
    cancelPendingRestore()
    saveScrollOnVisit()
  })
  document.addEventListener('turbo:load', scheduleRestore)
}

export default class extends Controller {}
